/**
 * @module @yimoka/i18n/react/create-i18n
 * @summary 高阶 API：一次注入端口、产出完整 i18n React 工具集
 * @author keep
 * @since 2026-05-21
 */

import { ConfigProvider, observer } from '@yimoka/react';
import type { i18n as I18n } from 'i18next';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { I18nextProvider, useTranslation, type UseTranslationResponse } from 'react-i18next';

import { createAppI18n } from '../create-app-i18n';
import type {
  BundledMap,
  CreateAppI18nOptions,
  FetchRemoteBundle,
  I18nStorage,
  I18nSyncPolicy,
  SubscribeAppResume,
  SyncManager,
} from '../types';

import { createWatchLang, type WatchLangProps } from './watch-lang';

/** createI18n 配置 */
export interface CreateI18nOptions<TBundled extends BundledMap> {
  /** 数据：语言短码 → namespace → 文案 */
  bundled: TBundled;
  /** 默认语言，缺省取 bundled 首键 */
  defaultLng?: keyof TBundled & string;
  /** 支持的语言列表，缺省取 bundled 全部键 */
  supportedLngs?: string[];

  /**
   * 端口 1：业务语言 hook（必传）。
   *
   * @remarks 返回值应与 bundled 顶层 key 对齐（推荐 BCP 47：`zh-CN` / `zh-Hant` / `en-US`）；
   * 包内通过监听该 hook 变化驱动 `i18n.changeLanguage`，业务侧切语言只需 `setLang(value)` 写入业务存储即可。
   */
  useLang: () => string;

  /** 端口 2：远程语言包拉取（可选） */
  fetchRemote?: FetchRemoteBundle;

  /** 端口 3：持久化（可选，最小 IO；包内自动 JSON 包装与 prefix 拼接） */
  storage?: I18nStorage;

  /** 端口 4：App 恢复使用时订阅（可选，Web visibilitychange / RN AppState / 小程序 onAppShow） */
  subscribeAppResume?: SubscribeAppResume;

  /** 端口 5：远程 loading 状态回调（可选） */
  onLoadingChange?: (loading: boolean) => void;

  /** 端口 6：切语言副作用（可选，i18next changeLanguage 完成后触发） */
  onLangChange?: (lang: string) => void;

  /** 同步策略部分覆盖 */
  sync?: Partial<I18nSyncPolicy>;

  /** i18next debug 开关 */
  debug?: boolean;
}

/** createI18n 返回值 */
export interface I18nReactInstance<TBundled extends BundledMap> {
  /** i18next 实例（已注入 Backend / initReactI18next） */
  i18n: I18n;
  /** 类型化 t（key 自 `defaultLng` bundled 推导） */
  t: (key: string, options?: Record<string, unknown>) => string;
  /** bundled / 默认语种 / 支持语种 */
  bundled: TBundled;
  defaultLng: keyof TBundled & string;
  supportedLngs: string[];
  /** SyncManager（手动 refresh / stop） */
  syncManager: SyncManager;
  /**
   * 应用根 Provider：业务在最外层（或 ConfigProvider 之后）挂载一次，
   * 内部固化 `<WatchLang>` → `<I18nextProvider>` → `<ConfigProvider scope={{ t }}>` 顺序。
   */
  I18nRoot: (props: WatchLangProps) => ReactNode;
  /**
   * Schema remount 钩子：基于 `i18n.resolvedLanguage`，
   * 业务在 `<Entity schemaKey={useSchemaI18nKey()} />` 中使用。
   */
  useSchemaI18nKey: () => string;
  /**
   * 绑定 createI18n 内部 i18n 实例的 useTranslation。
   *
   * @remarks monorepo 下业务侧勿直接从 `react-i18next` 引入，否则会命中另一份 Context 导致 t 退回 key。
   */
  useTranslation: (
    ns?: Parameters<typeof useTranslation>[0],
    options?: Omit<NonNullable<Parameters<typeof useTranslation>[1]>, 'i18n'>,
  ) => UseTranslationResponse<string, I18n>;
  /** 高级场景：直接拿 WatchLang 自定义嵌套 */
  WatchLang: (props: WatchLangProps) => ReactNode;
}

/**
 * 创建端口化的 i18n React 实例。
 *
 * 业务只需注入 `useLang`（必传）与可选端口（`fetchRemote` / `storage` / `subscribeAppResume` 等），
 * 即可拿到组装好的 `i18n / t / I18nRoot / useSchemaI18nKey`。
 *
 * @param options - 配置
 * @returns React 端 i18n 工具集
 *
 * @example
 * ```tsx
 * const { i18n, t, I18nRoot, useSchemaI18nKey } = createI18n({
 *   bundled,
 *   defaultLng: 'zh-CN',
 *   useLang: () => useAppLang(),
 *   fetchRemote: (lang) => httpGet(`/i18n?lang=${lang}`),
 *   storage: { get: AsyncStorage.getItem, set: AsyncStorage.setItem, prefix: 'app:i18n:' },
 *   subscribeAppResume: (onResume) => {
 *     const sub = AppState.addEventListener('change', (s) => s === 'active' && onResume());
 *     return () => sub.remove();
 *   },
 * });
 * ```
 */
export function createI18n<TBundled extends BundledMap>(
  options: CreateI18nOptions<TBundled>,
): I18nReactInstance<TBundled> {
  const {
    useLang,
    onLangChange,
    fetchRemote,
    storage,
    subscribeAppResume,
    onLoadingChange,
    sync,
    bundled,
    defaultLng,
    supportedLngs,
    debug,
  } = options;

  const appOptions: CreateAppI18nOptions<TBundled> = {
    bundled,
    defaultLng,
    supportedLngs,
    fetchRemote,
    storage,
    subscribeAppResume,
    onLoadingChange,
    sync,
    debug,
  };

  const i18nApp = createAppI18n(appOptions);

  const WatchLang = createWatchLang({
    i18n: i18nApp.i18n,
    useLanguage: useLang,
    getInitOptions: i18nApp.getInitOptions,
    backendModule: i18nApp.backendModule,
    syncManager: i18nApp.syncManager,
    onLangChange,
  });

  function useAppTranslation(
    ns?: Parameters<typeof useTranslation>[0],
    options?: Omit<NonNullable<Parameters<typeof useTranslation>[1]>, 'i18n'>,
  ): UseTranslationResponse<string, typeof i18nApp.i18n> {
    return useTranslation(ns, { ...options, i18n: i18nApp.i18n });
  }

  const SchemaScopeProvider = observer(({ children }: { children: ReactNode }) => {
    const { t } = useAppTranslation();
    const scope = useMemo(() => ({ t }), [t]);
    return <ConfigProvider value={{ scope }}>{children}</ConfigProvider>;
  });

  function I18nRoot({ children, fallback }: WatchLangProps) {
    return (
      <WatchLang fallback={fallback}>
        <I18nextProvider i18n={i18nApp.i18n}>
          <SchemaScopeProvider>{children}</SchemaScopeProvider>
        </I18nextProvider>
      </WatchLang>
    );
  }

  function useSchemaI18nKey(): string {
    const i18nInst = i18nApp.i18n;
    const [lang, setLang] = useState(() => i18nInst.resolvedLanguage ?? i18nInst.language);
    useEffect(() => {
      const handler = (next: string) => setLang(i18nInst.resolvedLanguage ?? next);
      i18nInst.on('languageChanged', handler);
      return () => {
        i18nInst.off('languageChanged', handler);
      };
    }, [i18nInst]);
    return lang;
  }

  return {
    i18n: i18nApp.i18n,
    t: i18nApp.t,
    bundled: i18nApp.bundled,
    defaultLng: i18nApp.defaultLng,
    supportedLngs: i18nApp.supportedLngs,
    syncManager: i18nApp.syncManager,
    I18nRoot,
    useSchemaI18nKey,
    useTranslation: useAppTranslation,
    WatchLang,
  };
}
